import React, { useCallback, useEffect } from 'react';

import { Badge, Stack, Text } from '@mantine/core';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import Table from 'components/Table';
import useAthletes from 'hooks/athlete/useAthletes';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AthleteList } from 'types';
import { formatPhoneNumber } from 'utils/formatters';

const AthletesTableContainer = ({
  searchFilters,
  tableData,
  setTableData,
  orderingValue,
  setOrderingValue,
}: {
  searchFilters: {
    searchTerm: string;
    isArrangeByLastName: boolean;
    athleteStatus: string;
    loginEnabled: string;
    packages: string[];
  };
  tableData: AthleteList[];
  setTableData: React.Dispatch<React.SetStateAction<AthleteList[]>>;
  orderingValue: string;
  setOrderingValue: React.Dispatch<React.SetStateAction<string>>;
  permission?: boolean;
}): JSX.Element => {
  const navigate = useNavigate();

  const ordering = orderingValue.startsWith('-') ? orderingValue.slice(1) : orderingValue;
  const ascending = !orderingValue.startsWith('-');

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthletes({
      ...searchFilters,
      ordering: orderingValue,
    });

  const handleHeaderClick = useCallback(
    (header: string): void => {
      const sameHeader = ordering === header;
      const ascendingTemp = sameHeader ? !ascending : true;
      setOrderingValue(ascendingTemp ? header : `-${header}`);
    },
    [ascending, ordering, setOrderingValue],
  );

  const handleNameClick = useCallback(
    (athlete: AthleteList): void => {
      navigate(`/users/athletes/${athlete.athleteId}/general-info`);
    },
    [navigate],
  );

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName' as const,
      Cell: ({ row }: CellProps<AthleteList>) => (
        <Stack spacing={0}>
          <Text weight='bold' sx={{ whiteSpace: 'nowrap' }}>
            {row.original.fullName}
          </Text>
          {row.original.connectedType === 'Linked' && (
            <div>
              <Badge color='kiloBlue' variant='filled' size='xs' radius='md'>
                Linked
              </Badge>
            </div>
          )}
          {row.original.connectedType === 'Dependent' && (
            <div>
              <Badge color='kiloBlue' variant='filled' size='xs' radius='md'>
                Dependent
              </Badge>
            </div>
          )}
        </Stack>
      ),
    },
    {
      Header: 'Status',
      accessor: 'athleteStatus' as const,
      Cell: ({ value }: CellProps<AthleteList>) => <AthleteStatusBadge status={value} />,
    },
    {
      Header: 'Email',
      accessor: 'email' as const,
      Cell: ({ row }: CellProps<AthleteList>) => <Text underline>{row.original.email}</Text>,
    },
    {
      Header: 'Phone',
      accessor: 'phone' as const,
      Cell: ({ value }: CellProps<AthleteList>) => formatPhoneNumber(value),
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <Table
      tableData={tableData}
      columns={columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={['firstName', 'email', 'athleteStatus', 'phone']}
      handleHeaderClick={handleHeaderClick}
      ordering={ordering}
      ascending={ascending}
      handleRowClick={handleNameClick}
      bgc='gray'
    />
  );
};

export default AthletesTableContainer;
